import React, { useCallback, useEffect, useState } from "react";

import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import Link from "@mui/material/Link";
import InputLabel from "@mui/material/InputLabel";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: `1px solid ${theme.palette.divider}`,
		borderRight: `1px solid ${theme.palette.divider}`,
		borderBottom: `1px solid ${theme.palette.divider}`,
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
	},
	contactTags: {
		marginTop: 4,
		padding: 6,
	},
	contactTagsSection: {
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary,
		padding: 8,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);
	const [tags, setTags] = useState([]);

	useEffect(() => {
		getTags(contact?.id); 
	}, [loading,contact]);

	const getTags = useCallback(( async (contactId) => {
		if (!contactId) return;
		const { data } = await api.get(`/tags/list/full/${contactId}`);
		setTags(data);
	})); 

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose} color="inherit">
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography>{contact.name}</Typography>
						<Typography>
							<Link href={`tel:${contact.number}`} color="inherit">
								{contact.number}
							</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
					<Paper square variant="outlined" className={classes.contactTagsSection}>
						<Typography variant="subtitle1">
							{i18n.t("contactDrawer.tags")}
						</Typography>
						{contact && 
							tags?.map(tag => (
								<Paper
									key={tag.id}
									square
									variant="outlined"
									className={classes.contactTags}
									style={{
										borderRadius: '20',
										backgroundColor: `${tag.color}`
									}}
								>
									<InputLabel style={{fontWeight: 'bold', borderRadius: '20px'}} >{tag.name}</InputLabel>
								</Paper>
							))}
					</Paper>
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
